import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { PAGE_NAME, PERMISSIONS, ROUTE } from "../../../../constants/constants";
import { Skeleton } from "../../../atoms/skeleton";
import {
    formattedDate,
    hasPermission,
    numberFormat,
} from "../../../../utils";
import { Divider } from "../../../atoms/divider";
import { Icon } from "../../../atoms/icon";
import { Dialog } from "../../../atoms/dialog";
import { ResponseType } from "../../../../types";
import { toast } from "react-toastify";
import { useDeleteCloudBudgetMutation, useGetCloudBudgetQuery } from "../../../../store/cost/costSlice";
import { Loader } from "../../../atoms/loader";

const CloudBudgetDetails = () => {
    const pageClassName = "p-cloudBudgetDetails";
    const { id } = useParams();

    const navigate = useNavigate();
    const [deleteApi, { isLoading: deleteLoading }] = useDeleteCloudBudgetMutation();

    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<string>();

    const dialogHandler = () => {
        setIsDialogOpen(true);
        setDeleteId(id);
    };

    const closeDialogHandler = () => {
        setIsDialogOpen(false);
    };

    const deleteHandler = async () => {
        closeDialogHandler();

        const response = (await deleteApi(deleteId as string)) as ResponseType;

        if (response?.data?.success) {
            toast.success(response.data.message);
            navigate(ROUTE.CLOUD_BUDGET);
        } else {
            toast.error(response?.error?.data?.message);
        }
    };

    const { data, isLoading } = useGetCloudBudgetQuery(id as string);

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.CLOUD_BUDGET_INFO}</p>
            <Loader isLoading={deleteLoading} >
                <div className={`${pageClassName}__container`}>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Budget ID
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.id ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Infra Name
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.saasTool ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Service Name
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? <Skeleton height="small" /> : data?.result?.name}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Description
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.description ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Start Date
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.startDate ? (
                                formattedDate(data?.result?.startDate)
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            End Date
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.endDate ? (
                                formattedDate(data?.result?.endDate)
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>



                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Monthly Budget
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                numberFormat(data?.result?.monthlyCostJPY, "JPY") ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Annual Budget
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                numberFormat(data?.result?.annualCostJPY, "JPY") ?? "-"
                            )}
                        </p>
                    </div>
                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Conversion Rate
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.conversionRate ?? "-"
                            }
                        </p>
                    </div>
                    <Divider />
                    <div className={`${pageClassName}__container__action`}>
                        {hasPermission(PERMISSIONS.PAYMENT_MANAGEMENT_CREATE.title) && (
                            <button
                                className={`${pageClassName}__container__action__button`}
                                onClick={() =>
                                    navigate(ROUTE.CLOUD_BUDGET + ROUTE.ADD_BUDGET, {
                                        state: {
                                            budget: data?.result,
                                        },
                                    })
                                }
                                type="button"
                                disabled={isLoading}
                            >
                                <Icon icon="copy" size="medium" /> Copy
                            </button>
                        )}
                        {hasPermission(PERMISSIONS.PAYMENT_MANAGEMENT_EDIT.title) && (
                            <button
                                className={`${pageClassName}__container__action__button`}
                                onClick={() =>
                                    navigate(ROUTE.CLOUD_BUDGET + ROUTE.EDIT_CLOUD_BUDGET, {
                                        state: {
                                            id: data?.result?.id,
                                        },
                                    })
                                }
                                type="button"
                                disabled={isLoading}
                            >
                                <Icon icon="edit" size="medium" /> Edit
                            </button>
                        )}
                        {hasPermission(PERMISSIONS.PAYMENT_MANAGEMENT_DELETE.title) && (
                            <button
                                className={`${pageClassName}__container__action__button ${pageClassName}__container__action__button--delete`}
                                onClick={dialogHandler}
                                type="button"
                                disabled={isLoading}
                            >
                                <Icon icon="delete" size="medium" /> Delete
                            </button>
                        )}
                    </div>
                </div>
            </Loader>
            <Dialog
                header="Delete Confirmation"
                body="Are you sure you want to delete this cloud budget?"
                isOpen={isDialogOpen}
                onClose={closeDialogHandler}
                successFn={deleteHandler}
                btn="Delete"
                icon="delete"
            />
        </div>
    );
};

export default CloudBudgetDetails;
